import { Drawer, ModalClose, DialogTitle, DialogContent, DialogActions, Button, Box, Grid, FormLabel, Typography, Chip, CircularProgress, Divider } from "@mui/joy";
import React from "react";
import { ProposalTrackingRecord } from "../Pages/ProposalTracking";
import { Util } from "../Helpers/Util";

// Interface for QBO estimate details returned from API
interface QboEstimateDetails {
	DocNumber: string;
	TotalAmt: number;
	TxnDate: string;
	CustomerName: string;
	TxnStatus: string;
	AcceptedBy: string | null;
	AcceptedDate: string | null;
	EmailStatus: string;
	DeliveryTime: string | null;
	DeliveryType: string | null;
}

// Interface for linked invoice records
interface LinkedInvoice {
	invoice_header_id: string;
	invoice_number: string;
	invoice_date: string;
	due_date: string;
	amount: number;
	balance: number;
	is_paid: boolean;
	building_name: string;
	status: string;
}

// Interface for assignment history records
interface AssignmentHistoryRecord {
	record_type: string;
	assignment_history_id: string | null;
	packing_list_id: string;
	reassignment_date: string | null;
	notes: string | null;
	previous_mechanic_name: string | null;
	current_mechanic_name: string | null;
	new_mechanic_name: string | null;
	reassigned_by_name: string | null;
}

interface ProposalDetailDialogProps {
	proposal: ProposalTrackingRecord | null;
	close: Function;
	open: boolean;
}

export default function ProposalDetailDialog(props: ProposalDetailDialogProps) {
	const [loading, setLoading] = React.useState(false);
	const [pdfPreview, setPdfPreview] = React.useState<string | null>(null);
	const [qboDetails, setQboDetails] = React.useState<QboEstimateDetails | null>(null);
	const [loadingDetails, setLoadingDetails] = React.useState(false);
	const [linkedInvoices, setLinkedInvoices] = React.useState<Array<LinkedInvoice>>([]);
	const [loadingInvoices, setLoadingInvoices] = React.useState(false);
	const [assignmentHistory, setAssignmentHistory] = React.useState<Array<AssignmentHistoryRecord>>([]);
	const [loadingHistory, setLoadingHistory] = React.useState(false);

	const m = props.proposal;

	// Reset preview and fetch QBO details when dialog opens with new proposal
	React.useEffect(() => {
		setPdfPreview(null);
		setQboDetails(null);
		setLinkedInvoices([]);
		setAssignmentHistory([]);
		if (props.open && m?.InvoiceNumber) {
			fetchQboDetails();
		}
		if (props.open && m?.InvoiceHeaderId) {
			fetchLinkedInvoices();
			fetchAssignmentHistory();
		}
	}, [props.proposal, props.open]);

	// Fetch detailed proposal info from QBO
	const fetchQboDetails = async () => {
		if (!m?.InvoiceNumber) return;
		setLoadingDetails(true);
		try {
			const response = await fetch("Proposal/GetProposalDetails", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ estimateNum: m.InvoiceNumber }),
			});
			if (response.ok) {
				const data = await response.json();
				setQboDetails(data);
			}
		} catch (error) {
			console.error("Failed to fetch QBO details:", error);
		} finally {
			setLoadingDetails(false);
		}
	};

	// Fetch linked invoices for this proposal
	const fetchLinkedInvoices = async () => {
		if (!m?.InvoiceHeaderId) return;
		setLoadingInvoices(true);
		try {
			const response = await fetch("Proposal/GetProposalLinkedInvoices", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ invoiceHeaderId: m.InvoiceHeaderId }),
			});
			if (response.ok) {
				const data = await response.json();
				setLinkedInvoices(data);
			}
		} catch (error) {
			console.error("Failed to fetch linked invoices:", error);
		} finally {
			setLoadingInvoices(false);
		}
	};

	// Fetch assignment history for this proposal
	const fetchAssignmentHistory = async () => {
		if (!m?.InvoiceHeaderId) return;
		setLoadingHistory(true);
		try {
			const response = await fetch("Proposal/GetProposalAssignmentHistory", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ invoiceHeaderId: m.InvoiceHeaderId }),
			});
			if (response.ok) {
				const data = await response.json();
				setAssignmentHistory(data);
			}
		} catch (error) {
			console.error("Failed to fetch assignment history:", error);
		} finally {
			setLoadingHistory(false);
		}
	};

	// Format currency for display
	const formatCurrency = (amount: number) => {
		if (amount == null) return "";
		return "$" + amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	};

	// Determine chip color based on status
	const getStatusColor = (status: string): "success" | "warning" | "neutral" => {
		if (!status) return "neutral";
		const lowerStatus = status.toLowerCase();
		if (lowerStatus === "paid" || lowerStatus === "accepted" || lowerStatus === "converted") return "success";
		if (lowerStatus === "pending") return "warning";
		return "neutral";
	};

	// Load PDF preview from QBO
	const loadPdfPreview = async () => {
		if (!m?.InvoiceNumber) return;
		setLoading(true);
		try {
			const response = await fetch("Proposal/GetPdfPreview", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ estimateNum: m.InvoiceNumber }),
			});
			if (response.ok) {
				const data = await response.json();
				setPdfPreview(data.image);
			}
		} catch (error) {
			console.error("Failed to load PDF preview:", error);
		} finally {
			setLoading(false);
		}
	};

	// Download full PDF
	const downloadPdf = async () => {
		if (!m?.InvoiceNumber || !m?.InvoiceHeaderId) return;
		setLoading(true);
		try {
			const response = await fetch("Proposal/GetPdf", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					estimateNum: m.InvoiceNumber,
					invoiceHeaderId: m.InvoiceHeaderId,
				}),
			});
			if (response.ok) {
				const fileName = await response.text();
				// Open PDF in new tab
				window.open("pdfs/" + fileName.replace(/"/g, ""), "_blank");
			}
		} catch (error) {
			console.error("Failed to download PDF:", error);
		} finally {
			setLoading(false);
		}
	};

	const sizes = {
		sm: 12,
		md: 6,
	};

	return (
		<Drawer anchor="right" open={props.open} size="lg">
			<ModalClose onClick={() => props.close()} />
			<DialogTitle>Proposal Details - {m?.InvoiceNumber}</DialogTitle>
			<DialogContent sx={{ padding: 2 }}>
				<Grid container spacing={2} sx={{ overflowX: "hidden" }}>
					{/* Proposal Information Section */}
					<Grid xs={12}>
						<FormLabel sx={{ typography: "title-md", fontWeight: "bold" }}>Proposal Information</FormLabel>
					</Grid>

					{/* Building Name */}
					<Grid {...sizes}>
						<Typography level="body-sm" sx={{ color: "text.secondary" }}>
							Building
						</Typography>
						<Typography level="body-md">{m?.BuildingName || "N/A"}</Typography>
					</Grid>

					{/* Invoice Number */}
					<Grid {...sizes}>
						<Typography level="body-sm" sx={{ color: "text.secondary" }}>
							Invoice/Estimate #
						</Typography>
						<Typography level="body-md">{m?.InvoiceNumber || "N/A"}</Typography>
					</Grid>

					{/* Total Amount */}
					<Grid {...sizes}>
						<Typography level="body-sm" sx={{ color: "text.secondary" }}>
							Total Amount
						</Typography>
						<Typography level="body-md" fontWeight="bold">
							{formatCurrency(m?.TotalAmount ?? 0)}
						</Typography>
					</Grid>

					{/* Invoice Date */}
					<Grid {...sizes}>
						<Typography level="body-sm" sx={{ color: "text.secondary" }}>
							Date
						</Typography>
						<Typography level="body-md">{Util.Formatter(m?.InvoiceDate, "D") || "N/A"}</Typography>
					</Grid>

					{/* Status */}
					<Grid {...sizes}>
						<Typography level="body-sm" sx={{ color: "text.secondary" }}>
							Status
						</Typography>
						<Chip variant="soft" color={getStatusColor(m?.Status ?? "")} size="sm" sx={{ mt: 0.5 }}>
							{m?.Status}
						</Chip>
					</Grid>

					{/* Approved */}
					<Grid {...sizes}>
						<Typography level="body-sm" sx={{ color: "text.secondary" }}>
							Approved
						</Typography>
						<Typography level="body-md">{m?.IsApproved ? "Yes" : "No"}</Typography>
					</Grid>

					{/* Description */}
					<Grid xs={12}>
						<Typography level="body-sm" sx={{ color: "text.secondary" }}>
							Description
						</Typography>
						<Typography level="body-md" sx={{ whiteSpace: "pre-wrap" }}>
							{m?.Description || "No description available"}
						</Typography>
					</Grid>

					<Grid xs={12}>
						<Divider sx={{ my: 2 }} />
					</Grid>

					{/* QBO Details Section - Approval Info */}
					<Grid xs={12}>
						<FormLabel sx={{ typography: "title-md", fontWeight: "bold" }}>QuickBooks Online Details</FormLabel>
						{loadingDetails && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
								<CircularProgress size="sm" />
								<Typography level="body-sm">Loading QBO details...</Typography>
							</Box>
						)}
					</Grid>

					{!loadingDetails && qboDetails && (
						<>
							{/* QBO Status */}
							<Grid {...sizes}>
								<Typography level="body-sm" sx={{ color: "text.secondary" }}>
									QBO Status
								</Typography>
								<Typography level="body-md">{qboDetails.TxnStatus || "N/A"}</Typography>
							</Grid>

							{/* Approved/Accepted */}
							<Grid {...sizes}>
								<Typography level="body-sm" sx={{ color: "text.secondary" }}>
									Accepted
								</Typography>
								<Chip variant="soft" color={qboDetails.TxnStatus === "Accepted" ? "success" : "neutral"} size="sm">
									{qboDetails.TxnStatus === "Accepted" ? "Yes" : "No"}
								</Chip>
							</Grid>

							{/* Accepted By */}
							<Grid {...sizes}>
								<Typography level="body-sm" sx={{ color: "text.secondary" }}>
									Accepted By
								</Typography>
								<Typography level="body-md">{qboDetails.AcceptedBy || "N/A"}</Typography>
							</Grid>

							{/* Accepted Date/Time */}
							<Grid {...sizes}>
								<Typography level="body-sm" sx={{ color: "text.secondary" }}>
									Accepted Date/Time
								</Typography>
								<Typography level="body-md">
									{qboDetails.AcceptedDate ? Util.Formatter(Util.ParseDate(qboDetails.AcceptedDate), "DT") : "N/A"}
								</Typography>
							</Grid>

							{/* Email Status (Sent) */}
							<Grid {...sizes}>
								<Typography level="body-sm" sx={{ color: "text.secondary" }}>
									Sent
								</Typography>
								<Chip variant="soft" color={qboDetails.EmailStatus === "EmailSent" ? "success" : "neutral"} size="sm">
									{qboDetails.EmailStatus === "EmailSent" ? "Yes" : "No"}
								</Chip>
							</Grid>

							{/* Sent Date/Time */}
							<Grid {...sizes}>
								<Typography level="body-sm" sx={{ color: "text.secondary" }}>
									Sent Date/Time
								</Typography>
								<Typography level="body-md">
									{qboDetails.DeliveryTime ? Util.Formatter(Util.ParseDate(qboDetails.DeliveryTime), "DT") : "N/A"}
								</Typography>
							</Grid>
						</>
					)}

					{!loadingDetails && !qboDetails && (
						<Grid xs={12}>
							<Typography level="body-sm" sx={{ color: "text.secondary", fontStyle: "italic" }}>
								Unable to load QBO details
							</Typography>
						</Grid>
					)}

					<Grid xs={12}>
						<Divider sx={{ my: 2 }} />
					</Grid>

					{/* Assignment History Section */}
					<Grid xs={12}>
						<FormLabel sx={{ typography: "title-md", fontWeight: "bold" }}>Assignment History</FormLabel>
						{loadingHistory && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
								<CircularProgress size="sm" />
								<Typography level="body-sm">Loading assignment history...</Typography>
							</Box>
						)}
					</Grid>

					{!loadingHistory && assignmentHistory.length > 0 && (
						<Grid xs={12}>
							{/* Current Assignment */}
							{assignmentHistory.filter(h => h.record_type === "current").map((record, idx) => (
								<Box key={idx} sx={{ mb: 2, p: 1.5, bgcolor: "background.level1", borderRadius: 0 }}>
									<Typography level="body-sm" sx={{ color: "text.secondary" }}>
										Currently Assigned To
									</Typography>
									<Typography level="body-md" fontWeight="bold">
										{record.current_mechanic_name || "Unassigned"}
									</Typography>
								</Box>
							))}

							{/* History Records */}
							{assignmentHistory.filter(h => h.record_type === "history").length > 0 && (
								<Box sx={{ mt: 1 }}>
									<Typography level="body-sm" sx={{ color: "text.secondary", mb: 1 }}>
										Previous Assignments
									</Typography>
									{assignmentHistory.filter(h => h.record_type === "history").map((record, idx) => (
										<Box key={idx} sx={{ mb: 1, p: 1, borderLeft: "3px solid", borderColor: "neutral.300", pl: 2 }}>
											<Typography level="body-sm">
												<strong>{record.previous_mechanic_name || "Unassigned"}</strong> â†’ <strong>{record.new_mechanic_name}</strong>
											</Typography>
											<Typography level="body-xs" sx={{ color: "text.secondary" }}>
												{record.reassignment_date ? Util.Formatter(Util.ParseDate(record.reassignment_date), "DT") : ""} by {record.reassigned_by_name}
											</Typography>
											{record.notes && (
												<Typography level="body-xs" sx={{ fontStyle: "italic", mt: 0.5 }}>
													Note: {record.notes}
												</Typography>
											)}
										</Box>
									))}
								</Box>
							)}
						</Grid>
					)}

					{!loadingHistory && assignmentHistory.length === 0 && (
						<Grid xs={12}>
							<Typography level="body-sm" sx={{ color: "text.secondary", fontStyle: "italic" }}>
								No assignment history available
							</Typography>
						</Grid>
					)}

					<Grid xs={12}>
						<Divider sx={{ my: 2 }} />
					</Grid>

					{/* Linked Invoices Section */}
					<Grid xs={12}>
						<FormLabel sx={{ typography: "title-md", fontWeight: "bold" }}>Linked Invoices</FormLabel>
						{loadingInvoices && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
								<CircularProgress size="sm" />
								<Typography level="body-sm">Loading linked invoices...</Typography>
							</Box>
						)}
					</Grid>

					{!loadingInvoices && linkedInvoices.length > 0 && (
						<Grid xs={12}>
							{linkedInvoices.map((invoice, idx) => (
								<Box key={idx} sx={{ mb: 1.5, p: 1.5, bgcolor: "background.level1", borderRadius: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Box>
										<Typography level="body-md" fontWeight="bold">
											Invoice #{invoice.invoice_number}
										</Typography>
										<Typography level="body-sm" sx={{ color: "text.secondary" }}>
											Created: {invoice.invoice_date ? Util.Formatter(Util.ParseDate(invoice.invoice_date), "D") : "N/A"}
											{" | "}
											Due: {invoice.due_date ? Util.Formatter(Util.ParseDate(invoice.due_date), "D") : "N/A"}
										</Typography>
										<Typography level="body-sm">
											Amount: {formatCurrency(invoice.amount)}
											{invoice.balance > 0 && ` (Balance: ${formatCurrency(invoice.balance)})`}
										</Typography>
									</Box>
									<Chip variant="soft" color={invoice.is_paid ? "success" : "warning"} size="sm">
										{invoice.is_paid ? "Paid" : "Unpaid"}
									</Chip>
								</Box>
							))}
						</Grid>
					)}

					{!loadingInvoices && linkedInvoices.length === 0 && (
						<Grid xs={12}>
							<Typography level="body-sm" sx={{ color: "text.secondary", fontStyle: "italic" }}>
								No linked invoices found
							</Typography>
						</Grid>
					)}

					<Grid xs={12}>
						<Divider sx={{ my: 2 }} />
					</Grid>

					{/* PDF Preview Section */}
					<Grid xs={12}>
						<FormLabel sx={{ typography: "title-md", fontWeight: "bold", mb: 1 }}>Document Preview</FormLabel>
						{loading && (
							<Box sx={{ textAlign: "center", py: 3 }}>
								<CircularProgress />
							</Box>
						)}
						{pdfPreview && !loading && (
							<Box sx={{ textAlign: "center", mb: 2 }}>
								<img src={`data:image/png;base64,${pdfPreview}`} alt="PDF Preview" style={{ maxWidth: "100%", border: "1px solid #ccc" }} />
							</Box>
						)}
						<Box sx={{ display: "flex", gap: 1 }}>
							{!pdfPreview && !loading && (
								<Button variant="outlined" color="primary" onClick={loadPdfPreview}>
									Load Preview
								</Button>
							)}
							<Button variant="solid" color="primary" onClick={downloadPdf} disabled={loading}>
								Download PDF
							</Button>
						</Box>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Box sx={{ padding: 2 }}>
					<Button size="lg" color="primary" onClick={() => props.close()} sx={{ mx: 1 }}>
						Close
					</Button>
				</Box>
			</DialogActions>
		</Drawer>
	);
}
